import { useEffect, useState } from 'react';

import { useSelector } from 'store';
import { isCallActiveStatus } from 'store/slices/twilioOutboundCall';
import {
    startCall,
    hangup,
    setMuted,
    denyRecordingConsent,
    setInputDevice,
    setSpeakerDevice,
    getInputVolume,
    resetCallState
} from 'utils/twilioCallController';

// Thin wrapper around the twilio slice + controller. Components should not
// import the controller directly — use this hook so the surface area is one
// place to evolve.
//
// The controller exports below (startCall, hangup, setMuted, setInputDevice,
// setSpeakerDevice, getInputVolume, resetCallState) are top-level function
// declarations in twilioCallController.js — module-scope-stable references
// that never get reassigned. Consumers can safely include them in useEffect /
// useCallback dependency arrays without churn (see AudioDevicePicker's
// getInputVolume polling effect). If the controller is ever refactored to
// reassign these exports, that contract breaks — wrap with useCallback or
// stash in a ref at the call site.
const useTwilioCall = () => {
    const {
        status,
        callSid,
        clinicId,
        patientId,
        patientSnapshot,
        toNumber,
        startedAt,
        muted,
        recordingConsentDenied,
        denyRecordingConsentInFlight,
        error,
        terminalMessage,
        audio
    } = useSelector((state) => state.twilioOutboundCall);

    // Live duration ticker — recomputes every second while a call is in progress
    // so the UI doesn't have to spin up its own timer.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (status !== 'inCall' || !startedAt) {
            return undefined;
        }
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [status, startedAt]);

    const durationSeconds = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0;
    const isActive = isCallActiveStatus(status);

    return {
        status,
        callSid,
        clinicId,
        patientId,
        patientSnapshot,
        toNumber,
        muted,
        recordingConsentDenied,
        denyRecordingConsentInFlight,
        error,
        terminalMessage,
        durationSeconds,
        isActive,
        audio,
        startCall,
        hangup,
        setMuted,
        denyRecordingConsent,
        setInputDevice,
        setSpeakerDevice,
        getInputVolume,
        resetCallState
    };
};

export default useTwilioCall;
